import { useEffect, useState } from "react";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";
import resolveImageUrl from "../../utils/imageUtils";
import toast from "react-hot-toast";

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};
import {
  Clock,
  CheckCircle,
  UserPlus,
  X,
  MapPin,
  AlertCircle,
  Calendar,
  MessageCircle,
  Loader2,
  Send,
  ThumbsDown,
  Search,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Flag = () => {
  const [properties, setProperties] = useState([]);
  const [circles, setCircles] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState("");
  const [inspectors, setInspectors] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingProperties, setFetchingProperties] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingComment, setMeetingComment] = useState("");
  const [requestingMeeting, setRequestingMeeting] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState("");
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");

  // Search state
  const [searchPin, setSearchPin] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [searchedProperties, setSearchedProperties] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const propertiesPerPage = 9;



  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;

  // Helper functions for ML data formatting
  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined" && str !== "n/a";
  };

  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const formatTwoDecimals = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const lowered = str.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    const n = toFiniteNumber(str);
    if (n === null) return str;
    return String(Math.round(n));
  };

  const getMLDisplayValue = (property, field) => {
    if (!property) return "N/A";
    const pickFirst = (keys) => {
      for (const key of keys) {
        const v = property?.[key];
        if (hasUsableValue(v)) return v;
      }
      return "";
    };

    let realValue = "";
    switch (field) {
      case 'floor':
        realValue = pickFirst(["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
        break;
      case 'landuse':
        realValue = pickFirst(["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
        break;
      case 'landarea':
        {
          const ml = pickFirst(["landarea_ml", "land_area_ml"]);
          const bucket = pickFirst(["landarea_combined_pred"]);
          const n = Number(String(ml ?? "").replace(/,/g, "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
      case 'coveredarea':
        {
          const ml = pickFirst(["coveredarea_ml", "covered_area_ml"]);
          const bucket = pickFirst(["coveredarea_combined_pred"]);
          const n = Number(String(ml ?? "").replace(/,/g, "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
    }

    if (hasUsableValue(realValue)) {
      if (field === "landarea" || field === "coveredarea") {
        // Check if realValue is a range string before trying to format as number
        const strVal = String(realValue);
        const isRange = strVal.toLowerCase().includes("greater") ||
          strVal.toLowerCase().includes("less") ||
          strVal.toLowerCase().includes("between");
        if (isRange) {
          return realValue;
        }
        const n = Number(String(realValue).replace(/,/g, "").trim());
        if (Number.isFinite(n) && !Number.isNaN(n)) return String(Math.round(n));
        return realValue;
      }
      const num = Number(realValue);
      if (Number.isFinite(num) && !isNaN(num)) {
        return Number.isInteger(num) ? num : Math.round(num);
      }
      return realValue;
    }
    return 'N/A';
  };

  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    const str = String(rangeStr).toLowerCase().trim();

    // Remove any units (sq yards, sq yds, sq ft, sq.yds, sq.ft, etc.)
    const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

    // Check for "greater than X" or "more than X"
    let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'greater', value: Number(match[1]) };
    }

    // Check for "less than X" or "under X"
    match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'less', value: Number(match[1]) };
    }

    // Check for "between X and Y"
    match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return {
        type: 'between',
        min: Number(match[1]),
        max: Number(match[2])
      };
    }

    // Try to parse as a single number
    const num = Number(cleanStr);
    if (Number.isFinite(num)) {
      return { type: 'exact', value: num };
    }

    return null;
  };

  const areValuesDifferent = (adminVal, mlVal, field) => {
    const normalize = (val) => {
      if (val === null || val === undefined) return null;
      const raw = String(val).trim();
      if (!raw) return null;
      const lowered = raw.toLowerCase();
      if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
      // Try to extract number for area fields
      if (field === "landarea" || field === "coveredarea") {
        const numStr = raw.replace(/[^0-9.]/g, '');
        const numeric = Number(numStr);
        if (Number.isFinite(numeric)) {
          return numeric;
        }
      }
      const numeric = Number(raw.replace(/,/g, ""));
      if (Number.isFinite(numeric)) return numeric;
      return lowered;
    };

    const a = normalize(adminVal);

    // For landarea and coveredarea, check if admin is within ML range
    if ((field === "landarea" || field === "coveredarea") && mlVal) {
      const range = parseRange(mlVal);
      if (range && typeof a === "number") {
        switch (range.type) {
          case "greater":
            return !(a > range.value); // highlight if admin is NOT greater
          case "less":
            return !(a < range.value); // highlight if admin is NOT less
          case "between":
            const inRange = a >= range.min && a <= range.max;
            return !inRange; // highlight if outside range
          case "exact":
            const exactMatch = Math.abs(a - range.value) <= 1e-9;
            return !exactMatch; // highlight if different
        }
      }
    }

    // For all other fields, use original comparison
    const m = normalize(mlVal);
    if (a === null || m === null) return false;
    if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
    return a !== m;
  };

  const hasMLData = (property) => {
    return (
      (property.floors_ml || property.floors_pred) &&
      (property.landuse_ml || property.land_use_pred)
    );
  };

  const handleImageClick = (e, imageUrl, property) => {
    e.stopPropagation();
    setFullscreenImageUrl(appendCacheBuster(imageUrl, property?._imgBust));
    setShowFullscreenImage(true);
    setRotation(0);
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async () => {
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      if (searchedProperties !== null) {
        setSearchedProperties((prev) =>
          prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
        );
      }
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setFullscreenImageUrl(
        appendCacheBuster(resolveImageUrl(selectedProperty.image), bust)
      );
      setRotation(0);
      toast.success("Image saved successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", { id: toastId });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const closeFullscreenImage = () => {
    setShowFullscreenImage(false);
    setFullscreenImageUrl("");
    setRotation(0);
  };

  useEffect(() => {
    fetchCircles();
    fetchProperties();
    fetchInspectors();
  }, []);

  useEffect(() => {
    const pin = selectedProperty?.pin;
    if (!selectedProperty || !pin) {
      setTimeline([]);
      setTimelineError("");
      setTimelineLoading(false);
      return;
    }

    const fetchTimeline = async () => {
      try {
        setTimelineLoading(true);
        setTimelineError("");
        const params = selectedProperty?.id ? { propertyId: selectedProperty.id } : {};
        const res = await axios.get(`/properties/timeline-id/${selectedProperty.id}`, { params });
        setTimeline(res.data?.timeline || []);
      } catch (err) {
        console.error("Failed to fetch timeline:", err);
        setTimeline([]);
        setTimelineError("Failed to load previous comments.");
      } finally {
        setTimelineLoading(false);
      }
    };

    fetchTimeline();
  }, [selectedProperty]);

  useEffect(() => {
    fetchProperties();
  }, [selectedCircle]);

  const fetchCircles = async () => {
    try {
      const res = await axios.get("/properties/circles");
      setCircles(res.data || []);
    } catch {
      console.error("Failed to load circles");
    }
  };

  const fetchProperties = async () => {
    try {
      setFetchingProperties(true);
      setLoading(true);
      const res = await axios.get("/properties/flagged", {
        params: {
          circle: selectedCircle || undefined,
        },
      });
      if (res.data.success) {
        // Keep properties either assigned directly to this supervisor OR flagged by this supervisor
        const filtered = (res.data.data || []).filter((p) =>
          (p.supervisor_user_id === userId || p.flagged_by === userId) &&
          (p.reassess === null || p.reassess === undefined)
        );
        // setProperties(filtered);
        const fetchBust = Date.now();
        setProperties(filtered.map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust })));
        setSearchedProperties(null);
        setSearchMessage("");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setFetchingProperties(false);
    }
  };

  const fetchInspectors = async () => {
    try {
      const res = await axios.get("/users/inspectors");
      setInspectors(res.data);
    } catch (err) {
      console.error("Fetch inspectors error:", err);
    }
  };

  // Search handler — searches all flagged properties by PIN
  const handleSearchByPin = async () => {
    const pin = searchPin.trim();
    if (!pin) {
      setSearchMessage("Please enter a PIN.");
      setSearchedProperties(null);
      return;
    }

    try {
      setSearching(true);
      setSearchMessage("");
      setSearchedProperties(null);

      // Fetch all flagged properties
      const res = await axios.get("/properties/flagged", {
        params: {
          circle: selectedCircle || undefined,
        },
      });

      // Keep properties either assigned directly to this supervisor OR flagged by this supervisor
      const list = (res.data?.data || []).filter((p) =>
        (p.supervisor_user_id === userId || p.flagged_by === userId) &&
        (p.reassess === null || p.reassess === undefined)
      );
      const found = list.find(
        (p) => String(p.pin || '').trim().toLowerCase() === pin.toLowerCase()
      );

      if (!found) {
        setSearchMessage("No flagged property found with this PIN.");
        setSearchedProperties([]);
        return;
      }

      setSearchedProperties([found]);
      setSearchMessage(`Found 1 property with PIN: ${pin}`);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setSearchMessage("Failed to search property by PIN.");
    } finally {
      setSearching(false);
    }
  };

  // Clear search and reset to all properties
  const handleClearSearch = () => {
    setSearchPin("");
    setSearchMessage("");
    setSearchedProperties(null);
    setSelectedCircle("");
    fetchProperties();
  };

  const calculateDaysAssigned = (assignedAt) => {
    if (!assignedAt) return null;
    const assignedDate = new Date(assignedAt);
    const diffTime = Math.abs(new Date() - assignedDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getLatestSupervisorAssignment = (property) => {
    const assignments = property?.assignments;
    if (!Array.isArray(assignments) || assignments.length === 0) return null;
    return assignments.reduce((latest, current) => {
      if (!latest) return current;
      const latestTime = latest.assigned_at ? new Date(latest.assigned_at).getTime() : 0;
      const currentTime = current.assigned_at ? new Date(current.assigned_at).getTime() : 0;
      return currentTime >= latestTime ? current : latest;
    }, null);
  };



  const handleAccept = async () => {
    if (!selectedProperty) return;
    try {
      await axios.post("/properties/accept", {
        propertyId: selectedProperty.id,
        userId,
      });
      fetchProperties();
      setSelectedProperty(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleReject = async () => {
    if (!selectedProperty) return;
    if (!rejectComment.trim()) {
      alert("Please enter a reason for rejection.");
      return;
    }

    try {
      setRejecting(true);
      await axios.post("/properties/reject", {
        propertyId: selectedProperty.id,
        userId,
        comment: rejectComment,
      });
      fetchProperties();
      setSelectedProperty(null);
      setShowRejectModal(false);
      setRejectComment("");
    } catch (err) {
      console.error(err);
      alert("Failed to reject property.");
    } finally {
      setRejecting(false);
    }
  };
  const handleRequestMeeting = async () => {
    if (!selectedProperty) return;
    if (!meetingComment.trim()) {
      alert("Please enter a reason or note for the meeting request.");
      return;
    }

    try {
      setRequestingMeeting(true);
      await axios.post("/properties/request-meeting", {
        propertyId: selectedProperty.id,
        userId,
        comment: meetingComment,
      });
      fetchProperties();
      setSelectedProperty(null);
      setShowMeetingModal(false);
      setMeetingComment("");
    } catch (err) {
      console.error(err);
      alert("Failed to request meeting.");
    } finally {
      setRequestingMeeting(false);
    }
  };
  // Get the properties to display
  const displayProperties =
    searchedProperties !== null ? searchedProperties : properties;

  // Pagination logic
  const indexOfLast = currentPage * propertiesPerPage;
  const indexOfFirst = indexOfLast - propertiesPerPage;
  const currentProperties = displayProperties.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(displayProperties.length / propertiesPerPage);

  // Reset to first page when search results change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchedProperties]);

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <Sidebar />
      <div className="flex-1 p-8 overflow-y-auto">
        {/* Fetching Loader */}
        {fetchingProperties && (
          <div className="fixed top-20 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-100 animate-pulse">
            <Loader2 className="animate-spin" size={16} />
            <span className="text-sm font-medium">
              Getting all flagged properties...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">
            Flagged Properties
          </h1>
          <p className="text-gray-600 mt-3 text-base italic border-l-4 border-blue-400 pl-4 bg-blue-50 py-2 rounded-r">
            Hello! These are properties you've flagged that are still awaiting the inspector's response. Once the inspector reassesses or marks "Do Not Reassess," the property will move automatically to Resolved or Not Resolved.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h2 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Search size={18} className="text-teal-600" />
              Search Property by PIN
            </h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={searchPin}
                onChange={(e) => setSearchPin(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchByPin();
                  }
                }}
                placeholder="Enter exact PIN..."
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
              <button
                onClick={handleSearchByPin}
                disabled={searching}
                className="px-5 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60 transition-colors"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
              {(searchMessage && searchMessage.includes("Found")) && (
                <button
                  onClick={handleClearSearch}
                  className="px-5 py-2 rounded-lg bg-gray-500 text-white font-medium hover:bg-gray-600 transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
            {searchMessage && (
              <p className={`mt-3 text-sm ${searchMessage.includes("Found") ? "text-green-600" : "text-red-600"}`}>
                {searchMessage}
              </p>
            )}
          </div>
        </div>

        {/* Circle Filter - Only show when not searching */}
        {!searchedProperties && (
          <div className="flex flex-wrap justify-center gap-4 mb-8 bg-white shadow-md rounded-xl p-4 border border-teal-100">
            <select
              value={selectedCircle}
              onChange={(e) => setSelectedCircle(e.target.value)}
              className="border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-gray-50 shadow-sm min-w-[180px]"
            >
              <option value="">All Circles</option>
              {circles.map((circle, i) => (
                <option key={i} value={circle}>
                  {circle}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Property Cards */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="animate-spin text-green-500" size={40} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {currentProperties.map((property) => {
                const days = calculateDaysAssigned(
                  property.assignments?.[0]?.assigned_at
                );
                const isOverdue = days > 10;
                const latestAssignment = getLatestSupervisorAssignment(property);

                return (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl shadow hover:shadow-lg border border-gray-200 overflow-hidden group relative cursor-pointer"
                    onClick={() => {
                      setSelectedProperty(property);
                    }}
                  >
                    <div className="relative">
                      <img
                        src={
                          property.image
                            ? appendCacheBuster(resolveImageUrl(property.image), property._imgBust)
                            : "https://via.placeholder.com/600x400?text=No+Image"
                        }
                        alt="property"
                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 left-3 bg-red-500/90 text-white text-xs font-semibold px-2 py-1 rounded-lg shadow">
                        Flagged
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h2 className="text-lg font-bold text-gray-900">
                          {property.circle} - {property.pin}
                        </h2>
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${property.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                            }`}
                        >
                          {property.status}
                        </span>
                      </div>

                      <div className="flex items-center text-gray-600 text-sm mb-4">
                        <MapPin size={14} className="mr-1 text-green-500" />
                        <span>{property.circle || "Property Location"}</span>
                      </div>

                      {property.supervisor_flag_comment && (
                        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                          <div className="text-xs font-semibold text-yellow-800 mb-1">Supervisor Flag Comment</div>
                          <div className="text-xs text-yellow-700 whitespace-pre-wrap line-clamp-2">
                            {property.supervisor_flag_comment}
                          </div>
                        </div>
                      )}
                      {property.meeting_requested == 1 && !property.meeting_resolved && (
                        <div className="mb-4 bg-purple-50 border border-purple-200 rounded-xl p-3">
                          <div className="text-xs font-semibold text-purple-800 mb-1 flex items-center gap-1">
                            <Calendar size={12} /> In-Person Meeting Requested
                          </div>
                        </div>
                      )}
                      {/* {latestAssignment?.reason && (
                        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-xl p-3">
                          <div className="text-xs font-semibold text-gray-800 mb-1">Supervisor Comment</div>
                          <div className="text-xs text-gray-700 whitespace-pre-wrap line-clamp-2">
                            {latestAssignment.reason}
                          </div>
                        </div>
                      )} */}
                      {latestAssignment?.reason && latestAssignment.reason !== property.supervisor_flag_comment && (
                        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-xl p-3">
                          <div className="text-xs font-semibold text-gray-800 mb-1">Supervisor Comment</div>
                          <div className="text-xs text-gray-700 whitespace-pre-wrap line-clamp-2">
                            {latestAssignment.reason}
                          </div>
                        </div>
                      )}

                      {(property.inspector_overall_explanation || property.inspector_last_comment || (property.comments && property.comments.some(c => c.user_role === 'inspector'))) && (
                        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-3">
                          <div className="text-xs font-semibold text-blue-800 mb-1">Inspector Reply</div>
                          <div className="text-xs text-blue-700 whitespace-pre-wrap line-clamp-2">
                            {property.inspector_overall_explanation || property.inspector_last_comment || (property.comments.find(c => c.user_role === 'inspector')?.comment)}
                          </div>
                        </div>
                      )}

                      {days && (
                        <div className="mb-4">
                          <div className="flex justify-between items-center text-sm mb-1">
                            <div className="flex items-center text-gray-700">
                              <Clock size={14} className="mr-1 text-gray-500" />
                              Days assigned: {days}
                            </div>
                            <span
                              className={`font-semibold ${isOverdue ? "text-red-600" : "text-green-600"
                                }`}
                            >
                              {isOverdue ? "Overdue" : "On track"}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${days <= 5
                                ? "bg-green-500"
                                : days <= 10
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                                }`}
                              style={{
                                width: `${Math.min((days / 30) * 100, 100)}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <MessageCircle size={14} />
                          {property.comments?.length || 0} comments
                        </span>
                        <div className="flex items-center text-green-600 text-sm font-semibold">
                          View details →
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Empty State */}
            {displayProperties.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
                <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  {searchedProperties !== null ? "No Property Found" : "No Flagged Properties"}
                </h3>
                <p className="text-gray-500 max-w-sm mx-auto">
                  {searchedProperties !== null
                    ? "No flagged property found with the entered PIN."
                    : "There are currently no flagged properties to review."}
                </p>
                {searchedProperties !== null && (
                  <button
                    onClick={handleClearSearch}
                    className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                  >
                    View All Properties
                  </button>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.max(p - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
                >
                  Prev
                </button>
                <span className="text-gray-700 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal with Updated Property Information Format */}
      {selectedProperty && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-fadeIn">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Property Details - {selectedProperty.pin}
                  {/* {hasMLData(selectedProperty) && (
                    <span className="ml-2 text-sm bg-purple-600 text-white px-2 py-1 rounded-full align-middle">
                      ML Predictions Available
                    </span>
                  )} */}
                </h2>
              </div>
              <button
                onClick={() => setSelectedProperty(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Property Image - Made Clickable */}
              <div
                className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden relative group"
              >
                {selectedProperty.image ? (
                  <>
                    <div
                      style={{ transform: `rotate(${rotation}deg)` }}
                      className="w-full transition-transform duration-300 ease-in-out cursor-pointer"
                      onClick={(e) => handleImageClick(e, resolveImageUrl(selectedProperty.image), selectedProperty)}
                    >
                      <img
                        src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                        alt={`Property ${selectedProperty.pin}`}
                        className="w-full h-auto max-h-[400px] object-contain transition-opacity group-hover:opacity-90 mx-auto"
                      />
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center pointer-events-none">
                      <span className="text-white bg-black/50 px-3 py-1 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Click to Enlarge
                      </span>
                    </div>
                    <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                      <button
                        onClick={handleRotate}
                        className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                        title="Rotate Image"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      {rotation !== 0 && (
                        <button
                          onClick={handleSaveRotation}
                          disabled={savingImageRotation}
                          className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                          title="Save Rotation"
                        >
                          {savingImageRotation ? (
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-64 flex items-center justify-center text-gray-500 text-lg">
                    No Image Available
                  </div>
                )}
              </div>

              {/* Property Information Table - Same format as inspector portal */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>

                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                    PIN: {selectedProperty.pin}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                    Circle: {selectedProperty.circle || "N/A"}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                    Status: {selectedProperty.status || "N/A"}
                  </span>
                </div>

                {selectedProperty.meeting_requested == 1 && !selectedProperty.meeting_resolved && (
                  <div className="mb-4 bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                      <Calendar size={16} /> In-person meeting requested
                      {selectedProperty.meeting_requested_at && (
                        <span className="text-xs font-normal text-purple-600">
                          on {new Date(selectedProperty.meeting_requested_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await axios.post("/properties/resolve-meeting", {
                            propertyId: selectedProperty.id,
                            userId,
                          });
                          fetchProperties();
                          setSelectedProperty((prev) => (prev ? { ...prev, meeting_resolved: 1 } : prev));
                        } catch (err) {
                          console.error(err);
                          alert("Failed to mark meeting as resolved.");
                        }
                      }}
                      className="text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                      Mark Resolved
                    </button>
                  </div>
                )}

                {(selectedProperty.inspector_overall_explanation ||
                  selectedProperty.inspector_floor_explanation ||
                  selectedProperty.inspector_landuse_explanation ||
                  selectedProperty.inspector_landarea_explanation ||
                  selectedProperty.inspector_coveredarea_explanation) && (
                    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="font-semibold text-blue-900">
                          {selectedProperty.reassess == 1
                            ? "Inspector Comments"
                            : "Reason for Not Reassessing"}
                        </h3>
                        {selectedProperty.inspector_explanation_created_at && (
                          <div className="text-sm text-blue-700">
                            {new Date(selectedProperty.inspector_explanation_created_at).toLocaleString()}
                          </div>
                        )}
                      </div>

                      {selectedProperty.inspector_overall_explanation && (
                        <p className="text-blue-800 whitespace-pre-wrap mb-2">
                          {selectedProperty.inspector_overall_explanation}
                        </p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {selectedProperty.inspector_floor_explanation && (
                          <div className="bg-white/70 border border-blue-100 rounded-lg p-2">
                            <div className="text-xs font-semibold text-blue-900">Floor</div>
                            <div className="text-sm text-blue-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_floor_explanation}
                            </div>
                          </div>
                        )}
                        {selectedProperty.inspector_landuse_explanation && (
                          <div className="bg-white/70 border border-blue-100 rounded-lg p-2">
                            <div className="text-xs font-semibold text-blue-900">Land Use</div>
                            <div className="text-sm text-blue-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_landuse_explanation}
                            </div>
                          </div>
                        )}
                        {selectedProperty.inspector_landarea_explanation && (
                          <div className="bg-white/70 border border-blue-100 rounded-lg p-2">
                            <div className="text-xs font-semibold text-blue-900">Land Area</div>
                            <div className="text-sm text-blue-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_landarea_explanation}
                            </div>
                          </div>
                        )}
                        {selectedProperty.inspector_coveredarea_explanation && (
                          <div className="bg-white/70 border border-blue-100 rounded-lg p-2">
                            <div className="text-xs font-semibold text-blue-900">Covered Area</div>
                            <div className="text-sm text-blue-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_coveredarea_explanation}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                <div className="overflow-x-auto">
                  <table className="w-full border rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-3 text-left font-medium">Feature</th>
                        <th className="p-3 text-left font-medium">Admin</th>
                        {/* <th className="p-3 text-left font-medium">Computer Vision</th> */}
                        {selectedProperty.reassess == 1 && (
                          <th className="p-3 text-left font-medium">Updated Assessment</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        className={`border-b ${areValuesDifferent(
                          selectedProperty.floor_admin,
                          getMLDisplayValue(selectedProperty, "floor"),
                          "floor"
                        )
                          ? "bg-red-100"
                          : ""
                          }`}
                      >
                        <td className="p-3 font-medium">Floor</td>
                        <td className="p-3">
                          {hasUsableValue(selectedProperty.floor_admin)
                            ? selectedProperty.floor_admin
                            : "N/A"}
                        </td>
                        {/* <td className="p-3">
                          {getMLDisplayValue(selectedProperty, "floor")}
                        </td> */}
                        {selectedProperty.reassess == 1 && (
                          <td className="p-3">
                            {(() => {
                              const inspectorVal = hasUsableValue(selectedProperty.floors_ml)
                                ? selectedProperty.floors_ml
                                : selectedProperty.floors_pred || selectedProperty.floor_pred;
                              const mlVal = getMLDisplayValue(selectedProperty, "floor");
                              const adminVal = selectedProperty.floor_admin;
                              let finalVal;
                              const inspectorSameAsML =
                                inspectorVal &&
                                mlVal &&
                                String(inspectorVal).trim().toLowerCase() ===
                                String(mlVal).trim().toLowerCase();
                              if (inspectorVal && !inspectorSameAsML) {
                                finalVal = inspectorVal;
                              } else {
                                finalVal = hasUsableValue(adminVal) ? adminVal : "N/A";
                              }
                              return <span className={inspectorVal && !inspectorSameAsML ? "bg-yellow-100 text-yellow-900 px-2 py-1 rounded" : "text-green-700"}>{finalVal}</span>;
                            })()}
                          </td>
                        )}
                      </tr>

                      <tr
                        className={`border-b ${areValuesDifferent(
                          selectedProperty.landuse_admin,
                          getMLDisplayValue(selectedProperty, "landuse"),
                          "landuse"
                        )
                          ? "bg-red-100"
                          : ""
                          }`}
                      >
                        <td className="p-3 font-medium">Land Use</td>
                        <td className="p-3">
                          {hasUsableValue(selectedProperty.landuse_admin)
                            ? selectedProperty.landuse_admin
                            : "N/A"}
                        </td>
                        {/* <td className="p-3">
                          {getMLDisplayValue(selectedProperty, "landuse")}
                        </td> */}
                        {selectedProperty.reassess == 1 && (
                          <td className="p-3">
                            {(() => {
                              const inspectorVal = hasUsableValue(selectedProperty.landuse_ml)
                                ? selectedProperty.landuse_ml
                                : selectedProperty.land_use_pred || selectedProperty.landuse_pred;
                              const mlVal = getMLDisplayValue(selectedProperty, "landuse");
                              const adminVal = selectedProperty.landuse_admin;
                              let finalVal;
                              const inspectorSameAsML =
                                inspectorVal &&
                                mlVal &&
                                String(inspectorVal).trim().toLowerCase() ===
                                String(mlVal).trim().toLowerCase();
                              if (inspectorVal && !inspectorSameAsML) {
                                finalVal = inspectorVal;
                              } else {
                                finalVal = hasUsableValue(adminVal) ? adminVal : "N/A";
                              }
                              return <span className={inspectorVal && !inspectorSameAsML ? "bg-yellow-100 text-yellow-900 px-2 py-1 rounded" : "text-green-700"}>{finalVal}</span>;
                            })()}
                          </td>
                        )}
                      </tr>

                      <tr
                        className={`border-b ${areValuesDifferent(
                          selectedProperty.landarea_admin,
                          getMLDisplayValue(selectedProperty, "landarea"),
                          "landarea"
                        )
                          ? "bg-red-100"
                          : ""
                          }`}
                      >
                        <td className="p-3 font-medium">Land Area</td>
                        <td className="p-3">
                          {hasUsableValue(selectedProperty.landarea_admin)
                            ? `${formatTwoDecimals(selectedProperty.landarea_admin) || selectedProperty.landarea_admin} sq.yards`
                            : "N/A"}
                        </td>
                        {/* <td className="p-3">
                          {getMLDisplayValue(selectedProperty, "landarea")}
                        </td> */}
                        {selectedProperty.reassess == 1 && (
                          <td className="p-3">
                            {(() => {
                              const inspectorVal = hasUsableValue(selectedProperty.landarea_ml)
                                ? selectedProperty.landarea_ml
                                : selectedProperty.landarea_combined_pred;
                              const mlVal = getMLDisplayValue(selectedProperty, "landarea");
                              const adminVal = selectedProperty.landarea_admin;
                              let finalVal;
                              const inspectorSameAsML =
                                inspectorVal &&
                                mlVal &&
                                String(inspectorVal).trim().toLowerCase() ===
                                String(mlVal).trim().toLowerCase();
                              if (inspectorVal && !inspectorSameAsML) {
                                const isRange =
                                  String(inspectorVal).toLowerCase().includes("greater") ||
                                  String(inspectorVal).toLowerCase().includes("less") ||
                                  String(inspectorVal).toLowerCase().includes("between");
                                finalVal = isRange
                                  ? inspectorVal
                                  : `${formatTwoDecimals(inspectorVal) || inspectorVal} sq.yards`;
                              } else {
                                finalVal = hasUsableValue(adminVal)
                                  ? `${formatTwoDecimals(adminVal) || adminVal} sq.yards`
                                  : "N/A";
                              }
                              return <span className={inspectorVal && !inspectorSameAsML ? "bg-yellow-100 text-yellow-900 px-2 py-1 rounded" : "text-green-700"}>{finalVal}</span>;
                            })()}
                          </td>
                        )}
                      </tr>

                      <tr
                        className={`border-b ${areValuesDifferent(
                          selectedProperty.coveredarea_admin,
                          getMLDisplayValue(selectedProperty, "coveredarea"),
                          "coveredarea"
                        )
                          ? "bg-red-100"
                          : ""
                          }`}
                      >
                        <td className="p-3 font-medium">Covered Area</td>
                        <td className="p-3">
                          {hasUsableValue(selectedProperty.coveredarea_admin)
                            ? `${formatTwoDecimals(selectedProperty.coveredarea_admin) || selectedProperty.coveredarea_admin} sq.ft`
                            : "N/A"}
                        </td>
                        {/* <td className="p-3">
                          {getMLDisplayValue(selectedProperty, "coveredarea")}
                        </td> */}
                        {selectedProperty.reassess == 1 && (
                          <td className="p-3">
                            {(() => {
                              const inspectorVal = hasUsableValue(selectedProperty.coveredarea_ml)
                                ? selectedProperty.coveredarea_ml
                                : selectedProperty.coveredarea_combined_pred;
                              const mlVal = getMLDisplayValue(selectedProperty, "coveredarea");
                              const adminVal = selectedProperty.coveredarea_admin;
                              let finalVal;
                              const inspectorSameAsML =
                                inspectorVal &&
                                mlVal &&
                                String(inspectorVal).trim().toLowerCase() ===
                                String(mlVal).trim().toLowerCase();
                              if (inspectorVal && !inspectorSameAsML) {
                                const isRange =
                                  String(inspectorVal).toLowerCase().includes("greater") ||
                                  String(inspectorVal).toLowerCase().includes("less") ||
                                  String(inspectorVal).toLowerCase().includes("between");
                                finalVal = isRange
                                  ? inspectorVal
                                  : `${formatTwoDecimals(inspectorVal) || inspectorVal} sq.ft`;
                              } else {
                                finalVal = hasUsableValue(adminVal)
                                  ? `${formatTwoDecimals(adminVal) || adminVal} sq.ft`
                                  : "N/A";
                              }
                              return <span className={inspectorVal && !inspectorSameAsML ? "bg-yellow-100 text-yellow-900 px-2 py-1 rounded" : "text-green-700"}>{finalVal}</span>;
                            })()}
                          </td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Display Inspector Comments First if available */}
              {(selectedProperty.inspector_overall_explanation ||
                selectedProperty.inspector_floor_explanation ||
                selectedProperty.inspector_landuse_explanation ||
                selectedProperty.inspector_landarea_explanation ||
                selectedProperty.inspector_coveredarea_explanation) && (
                  <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex justify-start">
                      <div className="max-w-[85%] border border-red-100 rounded-2xl px-5 py-4 shadow-md bg-gradient-to-br from-red-50 to-white text-red-900">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <div className="text-xs font-bold tracking-wide text-red-700 uppercase">
                            Inspector comment
                          </div>
                          {selectedProperty.inspector_explanation_created_at && (
                            <div className="text-[11px] text-red-600/80">
                              {new Date(selectedProperty.inspector_explanation_created_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                        <div className="space-y-3">
                          {selectedProperty.inspector_overall_explanation && (
                            <div className="text-sm leading-relaxed whitespace-pre-wrap">
                              {selectedProperty.inspector_overall_explanation}
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                            {selectedProperty.inspector_floor_explanation && (
                              <div key="floor" className="bg-white border border-red-100 rounded-xl p-3 shadow-sm">
                                <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Floor</div>
                                <div className="text-sm whitespace-pre-wrap">{selectedProperty.inspector_floor_explanation}</div>
                              </div>
                            )}
                            {selectedProperty.inspector_landuse_explanation && (
                              <div key="landuse" className="bg-white border border-red-100 rounded-xl p-3 shadow-sm">
                                <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Land Use</div>
                                <div className="text-sm whitespace-pre-wrap">{selectedProperty.inspector_landuse_explanation}</div>
                              </div>
                            )}
                            {selectedProperty.inspector_landarea_explanation && (
                              <div key="landarea" className="bg-white border border-red-100 rounded-xl p-3 shadow-sm">
                                <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Land Area</div>
                                <div className="text-sm whitespace-pre-wrap">{selectedProperty.inspector_landarea_explanation}</div>
                              </div>
                            )}
                            {selectedProperty.inspector_coveredarea_explanation && (
                              <div key="coveredarea" className="bg-white border border-red-100 rounded-xl p-3 shadow-sm">
                                <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Covered Area</div>
                                <div className="text-sm whitespace-pre-wrap">{selectedProperty.inspector_coveredarea_explanation}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">Comment Timeline</h3>
                  <div className="text-sm text-gray-500">Chat view</div>
                </div>

                {timelineLoading ? (
                  <div className="text-sm text-gray-600">Loading...</div>
                ) : timelineError ? (
                  <div className="text-sm text-red-600">{timelineError}</div>
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      // Backend timeline is already deduped and chronologically sorted —
                      // render it directly instead of re-adding synthetic supervisor bubbles.
                      const allItems = timeline || [];

                      if (allItems.length === 0) {
                        return <div className="text-sm text-gray-600">No previous comments found for this property.</div>;
                      }

                      return allItems.map((item) => {
                        const createdAt = item?.created_at ? new Date(item.created_at).toLocaleString() : "";
                        const role = String(item.user_role || "").toLowerCase();
                        const isSupervisor = item.type === "supervisor" || role === "supervisor";
                        const isInspector = item.type === "inspector_explanation" || role === "inspector";
                        const isAction = item.type === "action";

                        const actionText = (() => {
                          if (!isAction) return "";
                          if (String(item.action || "").startsWith("FLAG_PROPERTY")) return "Flagged";
                          if (item.action === "ACCEPT_PROPERTY") return "Resolved";
                          if (item.action === "UNFLAG_PROPERTY") return "Unflagged";
                          return String(item.action || "Action");
                        })();

                        const rows = item?.rows || {};
                        const rowEntries = [
                          ["Floor", rows.floor],
                          ["Land Use", rows.landuse],
                          ["Land Area", rows.landarea],
                          ["Covered Area", rows.coveredarea],
                        ].filter(([, v]) => hasUsableValue(v));

                        if (isAction) {
                          return (
                            <div key={item.id} className="flex justify-center">
                              <div className="px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                                {actionText}
                                {createdAt ? ` • ${createdAt}` : ""}
                              </div>
                            </div>
                          );
                        }

                        const sideClass = isSupervisor ? "justify-start" : "justify-end";
                        const bubbleClass = item.isYellow
                          ? "bg-yellow-50 border-yellow-200 text-yellow-900"
                          : isSupervisor
                            ? "bg-red-50 border-red-200 text-red-900"
                            : "bg-blue-50 border-blue-200 text-blue-900";
                        const headerLabel = item.isYellow ? "Supervisor Flag Comment" : isSupervisor ? "Supervisor comment" : isInspector ? "Inspector comment" : "Comment";

                        return (
                          <div key={item.id} className={`flex ${sideClass}`}>
                            <div className={`max-w-[85%] border rounded-2xl px-4 py-3 shadow-sm ${bubbleClass}`}>
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                <div className="text-xs font-semibold">
                                  {isSupervisor ? (
                                    `Supervisor ${item.user_name || "Supervisor"} Flagged this property`
                                  ) : isInspector ? (
                                    `Inspector ${item.user_name || ""} comment`
                                  ) : (
                                    headerLabel
                                  )}
                                </div>
                                {createdAt && <div className="text-[11px] opacity-80">{createdAt}</div>}
                              </div>

                              {item.type === "inspector_explanation" ? (
                                <div className="space-y-2">
                                  {hasUsableValue(item.overall) && (
                                    <div className="text-sm whitespace-pre-wrap">{item.overall}</div>
                                  )}
                                  {rowEntries.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {rowEntries.map(([k, v]) => (
                                        <div key={k} className="bg-white/70 border border-black/5 rounded-lg p-2">
                                          <div className="text-[11px] font-semibold opacity-80">{k}</div>
                                          <div className="text-sm whitespace-pre-wrap">{v}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm whitespace-pre-wrap">{item.text || ""}</div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>

            {selectedProperty.reassess == 1 ? (
              // Case 1: Inspector reassessed the property
              <div className="flex items-center justify-between gap-4 p-6 bg-gray-50 border-t border-gray-100 rounded-b-2xl sticky bottom-0">
                <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                  <CheckCircle size={18} /> Inspector accepted the flag and reassessed this property
                </div>
                <button
                  onClick={handleAccept}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg transition-colors shadow-sm"
                >
                  <CheckCircle size={18} /> Acknowledge & Close
                </button>
              </div>
            ) : selectedProperty.reassess == 0 ? (
              // Case 2: Inspector rejected the flag (Do Not Reassess)
              <div className="flex justify-end gap-4 p-6 bg-gray-50 border-t border-gray-100 rounded-b-2xl sticky bottom-0">
                <button
                  onClick={() => setShowMeetingModal(true)}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-lg transition-colors shadow-sm"
                >
                  <Calendar size={18} /> Ask Inspector for In-Person Meeting
                </button>
                <button
                  onClick={handleAccept}
                  className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-5 py-2.5 rounded-lg transition-colors shadow-sm"
                >
                  <CheckCircle size={18} /> Accept Inspector's Response
                </button>
              </div>
            ) : (
              // Case 3: No inspector response yet
              <div className="flex justify-between items-center gap-4 p-6 bg-gray-50 border-t border-gray-100 rounded-b-2xl sticky bottom-0">
                <p className="text-sm text-gray-500 italic">
                  Awaiting inspector's response — this property will move automatically once reviewed.
                </p>
                <button
                  onClick={() => setSelectedProperty(null)}
                  className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-5 py-2.5 rounded-lg transition-colors shadow-sm"
                >
                  Close
                </button>
              </div>
            )}
          </div>

        </div>
      )}

      {/* Fullscreen Image Modal with Rotation */}
      <AnimatePresence>
        {showFullscreenImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeFullscreenImage}
          >
            <div className="absolute top-6 right-6 flex space-x-4 z-10">
              <button
                onClick={handleRotate}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                title="Rotate Image"
              >
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
              {rotation !== 0 && (
                <button
                  onClick={handleSaveRotation}
                  disabled={savingImageRotation}
                  className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full transition-all shadow-lg flex items-center justify-center"
                  title="Save Rotation"
                >
                  {savingImageRotation ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>
              )}
              <button
                onClick={closeFullscreenImage}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
              >
                <span className="text-white text-2xl font-bold">×</span>
              </button>
            </div>
            <motion.div
              initial={{ scale: 0.9, opacity: 0, rotate: 0 }}
              animate={{ scale: 1, opacity: 1, rotate: rotation }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={fullscreenImageUrl}
                alt="Fullscreen property"
                className="max-w-[95vw] max-h-[95vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fadeIn">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">Reject Property</h3>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectComment("");
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Please provide a reason for rejecting this property. It will be sent back to the inspector for revision.
              </p>

              <textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all resize-none"
              />

              {!rejectComment.trim() && (
                <p className="text-red-500 text-sm mt-2">Rejection reason is required</p>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectComment("");
                }}
                className="px-5 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectComment.trim() || rejecting}
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-5 py-2.5 rounded-lg transition-colors shadow-sm"
              >
                {rejecting ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <ThumbsDown size={18} />
                )}
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Meeting Request Modal */}
      {showMeetingModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fadeIn">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">Request In-Person Meeting</h3>
              <button
                onClick={() => {
                  setShowMeetingModal(false);
                  setMeetingComment("");
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Let the inspector know why an in-person meeting is needed for this property.
              </p>

              <textarea
                value={meetingComment}
                onChange={(e) => setMeetingComment(e.target.value)}
                placeholder="Enter reason / notes for the meeting..."
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all resize-none"
              />

              {!meetingComment.trim() && (
                <p className="text-red-500 text-sm mt-2">A reason is required</p>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowMeetingModal(false);
                  setMeetingComment("");
                }}
                className="px-5 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestMeeting}
                disabled={!meetingComment.trim() || requestingMeeting}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-5 py-2.5 rounded-lg transition-colors shadow-sm"
              >
                {requestingMeeting ? <Loader2 className="animate-spin" size={18} /> : <Calendar size={18} />}
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Flag;
